import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { Redirect, Route } from "react-router-dom";
import { useTranslation } from "react-i18next";
import KeycloakIDPAdapter from "common/Adapters/IDPAdapter/KeycloakIDPAdapter";
import { redirector, usePathFactory } from "@sia/style-guide";
import GlobalContext from "contexts/GlobalContext";
import { MasterKeyContext } from "contexts/MasterKeyContext";
import NotLoggedInPage from "./pages/Error/NotLoggedinPage";
import PermissionDeniedPage from "./pages/Error/PermissionDeniedPage";

const PrivateRoute = ({ exact, path, render, allowedMemberTypes, masterKeyIsValid }) => {
  const { i18n } = useTranslation();
  const pathFactory = usePathFactory();
  const { user, isIDPInitialized, survey } = useContext(GlobalContext);
  const { isMasterKeyValid, isMasterKeyRestoring, isLoggingOut, setIsMasterKeyModalShown } = useContext(MasterKeyContext);
  const isLoggedIn = KeycloakIDPAdapter.isLoggedIn();
  const now = new Date();
  const surveyIsActive = new Date(survey?.activatedAt) < now && new Date(survey?.toClose) > now;
  const onRevisionPhase = new Date(survey?.toClose) < now && (survey?.finishedAt == null || new Date(survey.finishedAt) > now);
  const mayRequestSurveyAccess = Boolean(user && !user.isMemberTypeGroupOf("single") && (survey?.finishedAt || surveyIsActive || onRevisionPhase));
  const needsMasterKey = Boolean(
    masterKeyIsValid && isLoggedIn && mayRequestSurveyAccess && !isLoggingOut && !isMasterKeyRestoring && !isMasterKeyValid
  );
  const route = (content) => <Route exact={exact} path={path} render={content} />;

  useEffect(() => {
    if (isLoggingOut) setIsMasterKeyModalShown(false);
    else if (needsMasterKey) setIsMasterKeyModalShown(true);
  }, [isLoggingOut, needsMasterKey, setIsMasterKeyModalShown]);

  if (!isIDPInitialized) return route(() => null);
  if (isLoggingOut) return route(() => null);

  if (!isLoggedIn) {
    redirector.redirectToLoginFormBM(KeycloakIDPAdapter, i18n.language);
    return null;
  }

  if (!user) return route(() => <NotLoggedInPage />);

  if (allowedMemberTypes && !allowedMemberTypes.includes(user.getMemberType().getName())) {
    return route(() => <PermissionDeniedPage />);
  }

  if (masterKeyIsValid && isMasterKeyRestoring) return route(() => null);
  if (masterKeyIsValid && !isMasterKeyValid) {
    return route(() => <Redirect to={pathFactory.create("")} />);
  }

  return route(render);
};

PrivateRoute.propTypes = {
  path: PropTypes.string.isRequired,
  render: PropTypes.func.isRequired,
  exact: PropTypes.bool,
  allowedMemberTypes: PropTypes.array,
  masterKeyIsValid: PropTypes.bool,
};

export default PrivateRoute;
